/**
 * Modelo agnóstico de nota para guitarra, piano y melodica.
 */

import type { NoteName } from "./guitarEngine";
import { noteToSemitones } from "./guitarEngine";

export type PracticeHand = "L" | "R" | "both";

export interface PracticeNoteEvent {
  midi: number;
  /** Instante musical (divisiones) o ms según consumidor */
  start: number;
  duration: number;
  hand: PracticeHand;
  finger?: 1 | 2 | 3 | 4 | 5;
  degree?: string;
  step?: NoteName;
  voice?: number;
}

export type NotationLayout = "guitar-treble-8vb" | "piano-grand";

export interface PracticeSurfaceState {
  layout: NotationLayout;
  activeHand: "L" | "R" | "both";
}

export function midiToHand(midi: number, splitMidi = 60): PracticeHand {
  return midi >= splitMidi ? "R" : "L";
}

export function clampFinger(f: number): 1 | 2 | 3 | 4 | 5 {
  const n = Math.round(f);
  if (n < 1) return 1;
  if (n > 5) return 5;
  return n as 1 | 2 | 3 | 4 | 5;
}

export function noteNameFromMidi(midi: number, preferFlats = false): NoteName {
  return semitonesToNoteName(midi, preferFlats);
}

function semitonesToNoteName(semitones: number, preferFlats: boolean): NoteName {
  const names = preferFlats
    ? (["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"] as const)
    : (["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const);
  const pc = ((Math.round(semitones) % 12) + 12) % 12;
  return names[pc] as NoteName;
}

export function buildDegreeMap(
  root: NoteName,
  intervals: number[]
): Map<number, string> {
  const rootSemi = noteToSemitones(root);
  const degreeLabels = ["R", "2", "b3", "3", "4", "#4", "5", "b6", "6", "b7", "7"];
  const map = new Map<number, string>();
  intervals.forEach((interval, i) => {
    const midi = rootSemi + interval;
    map.set(((midi % 12) + 12) % 12, degreeLabels[i] ?? String(i + 1));
  });
  return map;
}
